import {
    type ReactNode
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatCurrency,
    formatDateTime,
    RadarChart,
    Chart
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    ChartDataRow,
    ChartConfig
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const stylesheet = StyleSheet.create({
    chart: {
        width: "100%"
    }
});

const NAME = "RadarChart";

const USAGE_CODE = `<Chart
    config={{
        visitors: {
            title: "Visitors",
            colorIndex: 1
        }
    }}
    description="January – June 2026, website"
    title="Monthly visitors"
>
    <RadarChart
        categoryKey="month"
        data={data}
    />
</Chart>`;

const VISITORS = [
    18600,
    30500,
    23700,
    27300,
    20900,
    21400
] as const;

const SKILLS = [
    {
        titleKey: "radarChartPage.skillAccessibility",
        mobile: 5,
        web: 8
    },
    {
        titleKey: "radarChartPage.skillFrontend",
        mobile: 5,
        web: 9
    },
    {
        titleKey: "radarChartPage.skillMobile",
        mobile: 9,
        web: 4
    },
    {
        titleKey: "radarChartPage.skillPerformance",
        mobile: 8,
        web: 7
    },
    {
        titleKey: "radarChartPage.skillTest",
        mobile: 7,
        web: 6
    },
    {
        titleKey: "radarChartPage.skillDesign",
        mobile: 6,
        web: 7
    }
] as const;

const TICKETS = [
    {
        titleKey: "radarChartPage.ticketShipping",
        tickets: 412
    },
    {
        titleKey: "radarChartPage.ticketPayment",
        tickets: 286
    },
    {
        titleKey: "radarChartPage.ticketReturns",
        tickets: 238
    },
    {
        titleKey: "radarChartPage.ticketAccount",
        tickets: 164
    },
    {
        titleKey: "radarChartPage.ticketCoupon",
        tickets: 122
    },
    {
        titleKey: "radarChartPage.ticketOther",
        tickets: 96
    }
] as const;

const DAYS = [
    {
        titleKey: "radarChartPage.dayMon",
        users: 12400
    },
    {
        titleKey: "radarChartPage.dayTue",
        users: 13100
    },
    {
        titleKey: "radarChartPage.dayWed",
        users: 13600
    },
    {
        titleKey: "radarChartPage.dayThu",
        users: 12900
    },
    {
        titleKey: "radarChartPage.dayFri",
        users: 11200
    },
    {
        titleKey: "radarChartPage.daySat",
        users: 7800
    },
    {
        titleKey: "radarChartPage.daySun",
        users: 6900
    }
] as const;

const REVENUE = [
    {
        previous: 2800000,
        current: 3400000
    },
    {
        previous: 3100000,
        current: 2900000
    },
    {
        previous: 3000000,
        current: 3800000
    },
    {
        previous: 3300000,
        current: 4200000
    },
    {
        previous: 3400000,
        current: 3600000
    },
    {
        previous: 3200000,
        current: 4500000
    }
] as const;

const CATEGORIES = [
    {
        titleKey: "radarChartPage.catElectronics",
        mobile: 980,
        store: 620,
        web: 1240
    },
    {
        titleKey: "radarChartPage.catClothing",
        mobile: 1320,
        store: 940,
        web: 860
    },
    {
        titleKey: "radarChartPage.catHome",
        mobile: 610,
        store: 1080,
        web: 720
    },
    {
        titleKey: "radarChartPage.catCosmetics",
        mobile: 880,
        store: 460,
        web: 540
    },
    {
        titleKey: "radarChartPage.catSports",
        mobile: 520,
        store: 690,
        web: 410
    },
    {
        titleKey: "radarChartPage.catBooks",
        mobile: 260,
        store: 180,
        web: 380
    }
] as const;

const CURRENCIES = {
    tr: "TRY",
    en: "USD"
} as const;

const SKILL_MAX = 10;

const TICKET_MAX = 500;

const DAY_MAX = 15000;

const REVENUE_MAX = 5000000;

const CATEGORY_MAX = 1500;

const MONTH_COUNT = 6;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("radarChartPage.seriesTitle"),
        id: "series"
    },
    {
        title: t("radarChartPage.dotsTitle"),
        id: "dots"
    },
    {
        title: t("radarChartPage.gridTitle"),
        id: "grid"
    },
    {
        title: t("radarChartPage.lineTitle"),
        id: "line"
    },
    {
        title: t("radarChartPage.interactiveTitle"),
        id: "interactive"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const RadarChartPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const months = Array.from({
        length: MONTH_COUNT
    }, (_, index) => formatDateTime({
        date: new Date(2026, index, 1),
        locale: activeLocale,
        options: {
            month: "short"
        }
    }));

    const formatMoney = (value: number) => formatCurrency({
        currency: CURRENCIES[language],
        options: {
            maximumFractionDigits: 1,
            notation: "compact"
        },
        locale: activeLocale,
        value
    });

    const formatScore = (value: number) => `${value} / ${SKILL_MAX}`;

    const visitorRows: Array<ChartDataRow> = VISITORS.map((visitors, index) => ({
        month: months[index] ?? "",
        visitors
    }));

    const skillRows: Array<ChartDataRow> = SKILLS.map((item) => ({
        category: t(item.titleKey),
        mobile: item.mobile,
        web: item.web
    }));

    const ticketRows: Array<ChartDataRow> = TICKETS.map((item) => ({
        category: t(item.titleKey),
        tickets: item.tickets
    }));

    const dayRows: Array<ChartDataRow> = DAYS.map((item) => ({
        category: t(item.titleKey),
        users: item.users
    }));

    const revenueRows: Array<ChartDataRow> = REVENUE.map((item, index) => ({
        category: months[index] ?? "",
        previous: item.previous,
        current: item.current
    }));

    const categoryRows: Array<ChartDataRow> = CATEGORIES.map((item) => ({
        category: t(item.titleKey),
        mobile: item.mobile,
        store: item.store,
        web: item.web
    }));

    const renderChart = (chart: ReactNode, props: {
        isLegendToggleable?: boolean;
        config: ChartConfig;
        description: string;
        title: string;
        note?: string;
    }) => <View
        style={[
            stylesheet.chart,
            {
                maxWidth: shapeConfigs.contentMaxWidth * 2 / 3
            }
        ]}
    >
        <Chart
            isLegendToggleable={props.isLegendToggleable}
            description={props.description}
            spreadBehaviour="stretch"
            isAnimatedOnMount={true}
            config={props.config}
            title={props.title}
            note={props.note}
        >
            {chart}
        </Chart>
    </View>;

    const webMobileConfig: ChartConfig = {
        mobile: {
            title: t("radarChartPage.seriesMobile"),
            colorIndex: 2
        },
        web: {
            title: t("radarChartPage.seriesWeb"),
            colorIndex: 1
        }
    };

    return <Article>
        <ComponentHeader
            lead={t("radarChartPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("radarChartPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("radarChartPage.overviewCaption")}
            >
                {
                    renderChart(<RadarChart
                        categoryTitle={t("radarChartPage.month")}
                        categoryKey="month"
                        data={visitorRows}
                        maxValue={40000}
                    />, {
                        description: t("radarChartPage.visitorDescription"),
                        config: {
                            visitors: {
                                title: t("radarChartPage.visitors"),
                                colorIndex: 1
                            }
                        },
                        title: t("radarChartPage.visitorTitle"),
                        note: t("radarChartPage.visitorNote")
                    })
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("radarChartPage.seriesTitle")}
            id="series"
        >
            <Paragraph>
                {t("radarChartPage.seriesText")}
            </Paragraph>
            <Preview
                caption={t("radarChartPage.seriesCaption")}
            >
                {
                    renderChart(<RadarChart
                        categoryTitle={t("radarChartPage.skill")}
                        valueFormatter={formatScore}
                        categoryKey="category"
                        maxValue={SKILL_MAX}
                        data={skillRows}
                    />, {
                        description: t("radarChartPage.skillDescription"),
                        title: t("radarChartPage.skillTitle"),
                        note: t("radarChartPage.skillNote"),
                        config: webMobileConfig
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("radarChartPage.dotsTitle")}
            id="dots"
        >
            <Paragraph>
                {t("radarChartPage.dotsText")}
            </Paragraph>
            <Preview
                caption={t("radarChartPage.dotsCaption")}
            >
                {
                    renderChart(<RadarChart
                        categoryTitle={t("radarChartPage.ticketCategory")}
                        categoryKey="category"
                        maxValue={TICKET_MAX}
                        data={ticketRows}
                        isShowDots={true}
                    />, {
                        description: t("radarChartPage.ticketDescription"),
                        config: {
                            tickets: {
                                title: t("radarChartPage.tickets"),
                                colorIndex: 1
                            }
                        },
                        title: t("radarChartPage.ticketTitle")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("radarChartPage.gridTitle")}
            id="grid"
        >
            <Paragraph>
                {t("radarChartPage.gridText")}
            </Paragraph>
            <Preview
                caption={t("radarChartPage.gridCaption")}
            >
                {
                    renderChart(<RadarChart
                        categoryTitle={t("radarChartPage.day")}
                        categoryKey="category"
                        maxValue={DAY_MAX}
                        gridShape="circle"
                        data={dayRows}
                    />, {
                        description: t("radarChartPage.dayDescription"),
                        config: {
                            users: {
                                title: t("radarChartPage.users"),
                                colorIndex: 1
                            }
                        },
                        title: t("radarChartPage.dayTitle")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("radarChartPage.lineTitle")}
            id="line"
        >
            <Paragraph>
                {t("radarChartPage.lineText")}
            </Paragraph>
            <Preview
                caption={t("radarChartPage.lineCaption")}
            >
                {
                    renderChart(<RadarChart
                        categoryTitle={t("radarChartPage.month")}
                        valueFormatter={formatMoney}
                        maxValue={REVENUE_MAX}
                        categoryKey="category"
                        data={revenueRows}
                        isFilled={false}
                    />, {
                        description: t("radarChartPage.revenueDescription"),
                        config: {
                            previous: {
                                title: t("radarChartPage.revenuePrevious"),
                                colorIndex: 2
                            },
                            current: {
                                title: t("radarChartPage.revenueCurrent"),
                                colorIndex: 1
                            }
                        },
                        title: t("radarChartPage.revenueTitle")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("radarChartPage.interactiveTitle")}
            id="interactive"
        >
            <Paragraph>
                {t("radarChartPage.interactiveText")}
            </Paragraph>
            <Preview
                caption={t("radarChartPage.interactiveCaption")}
            >
                {
                    renderChart(<RadarChart
                        categoryTitle={t("radarChartPage.productCategory")}
                        maxValue={CATEGORY_MAX}
                        categoryKey="category"
                        data={categoryRows}
                    />, {
                        description: t("radarChartPage.categoryDescription"),
                        config: {
                            ...webMobileConfig,
                            store: {
                                title: t("radarChartPage.seriesStore"),
                                colorIndex: 3
                            }
                        },
                        title: t("radarChartPage.categoryTitle"),
                        isLegendToggleable: true
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("radarChartPage.a11yPlot"),
                    t("radarChartPage.a11yKeyboard"),
                    t("radarChartPage.a11yLegend"),
                    t("radarChartPage.a11yTable"),
                    t("radarChartPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "Array<Record<string, number | string>>",
                        description: t("radarChartPage.propData"),
                        name: "data"
                    },
                    {
                        description: t("radarChartPage.propCategoryKey"),
                        name: "categoryKey",
                        type: "string"
                    },
                    {
                        description: t("radarChartPage.propSeriesKeys"),
                        type: "Array<string>",
                        name: "seriesKeys"
                    },
                    {
                        description: t("radarChartPage.propGridShape"),
                        type: "\"polygon\" | \"circle\" | \"none\"",
                        defaultValue: "\"polygon\"",
                        name: "gridShape"
                    },
                    {
                        description: t("radarChartPage.propIsFilled"),
                        defaultValue: "true",
                        name: "isFilled",
                        type: "boolean"
                    },
                    {
                        description: t("radarChartPage.propIsShowDots"),
                        defaultValue: "false",
                        name: "isShowDots",
                        type: "boolean"
                    },
                    {
                        description: t("radarChartPage.propMaxValue"),
                        name: "maxValue",
                        type: "number"
                    },
                    {
                        description: t("radarChartPage.propValueFormatter"),
                        type: "(value: number) => string",
                        name: "valueFormatter"
                    },
                    {
                        type: "(value: string | number, index: number) => string",
                        description: t("radarChartPage.propCategoryFormatter"),
                        name: "categoryFormatter"
                    },
                    {
                        description: t("radarChartPage.propCategoryTitle"),
                        name: "categoryTitle",
                        type: "string"
                    },
                    {
                        description: t("radarChartPage.propPlotSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "plotSize"
                    },
                    {
                        description: t("radarChartPage.propSummary"),
                        name: "summary",
                        type: "string"
                    },
                    {
                        description: t("radarChartPage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        type: "boolean"
                    },
                    {
                        description: t("radarChartPage.propTooltipBehaviour"),
                        type: "\"auto\" | \"follow\" | \"pinned\"",
                        name: "tooltipBehaviour"
                    },
                    {
                        type: "(content: ChartTooltipContent) => ReactNode",
                        description: t("radarChartPage.propRenderTooltip"),
                        name: "renderTooltip"
                    },
                    {
                        description: t("radarChartPage.propOnActiveChange"),
                        type: "(index: number | undefined) => void",
                        name: "onActiveChange"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("radarChartPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("radarChartPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("radarChartPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default RadarChartPage;
